import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Building2, Users, BadgeCheck, GraduationCap, Eye, Home as HomeIcon,
    Handshake, Plus, Loader2, Mail, Trash2, X, Copy as CopyIcon,
    Banknote, Crown, ExternalLink,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AgencyDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await api.get("/my/agency");
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) { navigate("/login?next=/agency"); return; }
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (!user) return null;

    const isOwnerOrAdmin = data?.agency && (data.role === "agency_owner" || data.role === "agency_admin");

    return (
        <div data-testid="agency-dashboard-page" className="bg-background min-h-screen">
            <section className="bg-ocean-deep-grad text-white py-10 lg:py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-3">
                        <Building2 className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            Agency Dashboard
                        </span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-2">
                        {data?.agency ? data.agency.name : "Agency-level team accounts"}
                    </h1>
                    <p className="font-app text-sm sm:text-base text-white/85 max-w-3xl">
                        {data?.agency
                            ? "Aggregated team performance, member roster, and invitations."
                            : "Bring your team under one banner. Listings show your firm name. Aggregated dashboard shows team totals."}
                    </p>
                </div>
            </section>

            <section className="py-10 lg:py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading && <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin text-coral mx-auto" /></div>}

                    {!loading && data && !data.agency && (
                        <Card className="p-8 text-center bg-card" data-testid="agency-empty-state">
                            <Building2 className="h-12 w-12 text-coral mx-auto mb-3" />
                            <h2 className="font-display text-xl font-bold text-foreground mb-2">You're not in an agency yet.</h2>
                            <p className="font-app text-sm text-muted-foreground max-w-md mx-auto mb-5">
                                Create an agency to invite your team. All members' listings + JVs roll up to one dashboard.
                            </p>
                            <Button onClick={() => setCreateOpen(true)} data-testid="create-agency-btn" className="bg-coral hover:bg-coral-deep text-white">
                                <Plus className="h-4 w-4 mr-2" /> Create my agency
                            </Button>
                        </Card>
                    )}

                    {!loading && data?.agency && (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="agency-stats">
                                <Stat icon={Users} label="Members" value={data.stats?.member_count || 0} />
                                <Stat icon={BadgeCheck} label="Verified" value={data.stats?.verified_count || 0} />
                                <Stat icon={GraduationCap} label="Certified" value={data.stats?.certified_count || 0} accent />
                                <Stat icon={HomeIcon} label="Active listings" value={data.stats?.listings_active || 0} />
                                <Stat icon={Handshake} label="JVs" value={data.stats?.jvs_total || 0} />
                                <Stat icon={Eye} label="Total views" value={(data.stats?.total_views || 0).toLocaleString()} accent />
                                <Stat icon={HomeIcon} label="All-time listings" value={data.stats?.listings_total || 0} />
                                <Stat icon={Mail} label="Open invites" value={data.open_invites?.length || 0} />
                            </div>

                            {/* Payouts setup + Agency tier — owner-only */}
                            {(data.role === "agency_owner") && (
                                <div className="grid lg:grid-cols-2 gap-3 mb-8" data-testid="agency-revenue-cards">
                                    <StripeConnectCard agency={data.agency} />
                                    <AgencyTierCard agency={data.agency} />
                                </div>
                            )}

                            {/* Roster */}
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-display text-xl lg:text-2xl font-bold text-foreground">Team roster</h2>
                                {isOwnerOrAdmin && (
                                    <Button onClick={() => setInviteOpen(true)} data-testid="invite-agent-btn" className="bg-coral hover:bg-coral-deep text-white">
                                        <Plus className="h-4 w-4 mr-2" /> Invite agent
                                    </Button>
                                )}
                            </div>
                            <div className="space-y-2 mb-8" data-testid="agency-roster">
                                {(data.members || []).map((m) => (
                                    <MemberRow
                                        key={m.id}
                                        member={m}
                                        isOwner={m.id === data.agency.owner_user_id}
                                        canManage={isOwnerOrAdmin && m.id !== data.agency.owner_user_id}
                                        onRemove={async () => {
                                            if (!window.confirm(`Remove ${m.full_name || m.email}?`)) return;
                                            try {
                                                await api.delete(`/agencies/${data.agency.id}/members/${m.id}`);
                                                toast.success("Removed.");
                                                await reload();
                                            } catch (err) {
                                                toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
                                            }
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Open invites */}
                            {isOwnerOrAdmin && data.open_invites?.length > 0 && (
                                <>
                                    <h3 className="font-display text-base font-bold text-foreground mb-2">Pending invites</h3>
                                    <div className="space-y-2" data-testid="agency-pending-invites">
                                        {data.open_invites.map((inv) => (
                                            <Card key={inv.id} className="p-3 bg-card flex items-center gap-2 flex-wrap text-sm font-app">
                                                <Mail className="h-4 w-4 text-coral shrink-0" />
                                                <span className="text-foreground">{inv.email}</span>
                                                <Badge variant="outline" className="text-[10px] capitalize">{inv.role.replace("_", " ")}</Badge>
                                                <span className="text-muted-foreground text-xs">expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                                            </Card>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </section>

            {createOpen && <CreateAgencyDialog onClose={() => setCreateOpen(false)} onSuccess={() => { setCreateOpen(false); reload(); }} />}
            {inviteOpen && data?.agency && (
                <InviteDialog agencyId={data.agency.id} onClose={() => setInviteOpen(false)} onSuccess={() => { setInviteOpen(false); reload(); }} />
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <Card className={`p-4 ${accent ? "bg-coral/5 dark:bg-coral/10 border-coral/20" : "bg-card"}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`h-3.5 w-3.5 ${accent ? "text-coral" : "text-muted-foreground"}`} />
                <span className="font-app text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            </div>
            <div className={`font-display text-2xl font-bold tabular-nums ${accent ? "text-coral" : "text-foreground"}`}>
                {value}
            </div>
        </Card>
    );
}

function MemberRow({ member, isOwner, canManage, onRemove }) {
    return (
        <Card className="p-3 bg-card flex items-center gap-3 flex-wrap" data-testid={`agency-member-${member.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-sm font-bold text-foreground truncate">{member.full_name || member.email}</span>
                    {isOwner && <Badge className="bg-coral text-white text-[10px]">Owner</Badge>}
                    {member.agency_role === "agency_admin" && !isOwner && (
                        <Badge variant="outline" className="text-[10px]">Admin</Badge>
                    )}
                    {member.agent_verified && (
                        <BadgeCheck className="h-3.5 w-3.5 text-fiji-green" aria-label="Verified" />
                    )}
                    {member.agent_trained && (
                        <GraduationCap className="h-3.5 w-3.5 text-coral" aria-label="Certified" />
                    )}
                </div>
                {member.email && (
                    <div className="font-app text-[11px] text-muted-foreground truncate">{member.email}</div>
                )}
            </div>
            {canManage && (
                <Button
                    onClick={onRemove}
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`agency-member-remove-${member.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            )}
        </Card>
    );
}

function CreateAgencyDialog({ onClose, onSuccess }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (name.trim().length < 2) { toast.error("Name needs at least 2 characters"); return; }
        setSubmitting(true);
        try {
            await api.post("/agencies", { name: name.trim(), description: description.trim() || undefined });
            toast.success("Agency created. You're the owner.");
            onSuccess();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not create");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="create-agency-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Create your agency</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        You become the agency owner. Invite agents to join your banner.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Agency name *</Label>
                        <Input
                            data-testid="agency-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Sunrise Realty Fiji"
                            className="bg-background text-foreground"
                            required
                            minLength={2}
                            maxLength={80}
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Description</Label>
                        <Textarea
                            data-testid="agency-desc-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Boutique iTaukei + freehold specialist serving the Coral Coast since 2019."
                            rows={3}
                            className="bg-background text-foreground resize-none"
                            maxLength={400}
                        />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="create-agency-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Create agency
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function InviteDialog({ agencyId, onClose, onSuccess }) {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("agent");
    const [submitting, setSubmitting] = useState(false);
    const [createdToken, setCreatedToken] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post(`/agencies/${agencyId}/invites`, { email: email.trim().toLowerCase(), role });
            setCreatedToken(data.token);
            toast.success("Invite created. Share the link below.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not invite");
        } finally {
            setSubmitting(false);
        }
    };

    const inviteUrl = createdToken ? `${window.location.origin}/agency/invite/${createdToken}` : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            toast.success("Link copied.");
        } catch {
            window.prompt("Copy invite link:", inviteUrl);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="invite-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Invite an agent</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        We mint a single-use link valid for 14 days. They click → log in → join your agency.
                    </DialogDescription>
                </DialogHeader>
                {!createdToken ? (
                    <form onSubmit={submit} className="space-y-3 pt-2">
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Their email *</Label>
                            <Input
                                data-testid="invite-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="bg-background text-foreground"
                                placeholder="agent@example.com"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Role</Label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger data-testid="invite-role-select" className="bg-background text-foreground"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="agent">Agent</SelectItem>
                                    <SelectItem value="agency_admin">Agency admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter className="pt-2">
                            <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                            <Button type="submit" disabled={submitting} data-testid="invite-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Mint invite link
                            </Button>
                        </DialogFooter>
                    </form>
                ) : (
                    <div className="space-y-3 pt-2" data-testid="invite-link-box">
                        <Label className="text-xs font-app text-muted-foreground">Share this link with {email}</Label>
                        <div className="flex items-center gap-2">
                            <Input readOnly value={inviteUrl} className="bg-background text-foreground text-xs font-mono" />
                            <Button onClick={copy} variant="outline" data-testid="invite-copy-btn">
                                <CopyIcon className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                        <p className="font-app text-[11px] text-muted-foreground">
                            Single-use. Expires in 14 days. They must log in with the same email to accept.
                        </p>
                        <DialogFooter className="pt-2">
                            <Button onClick={onSuccess} className="bg-ocean-deep hover:bg-ocean-deep/90 text-white font-app w-full">
                                <X className="h-4 w-4 mr-2" /> Done
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function StripeConnectCard({ agency }) {
    const [status, setStatus] = useState({
        connected: !!agency.stripe_connect_account_id,
        payouts_enabled: !!agency.stripe_connect_payouts_enabled,
        charges_enabled: !!agency.stripe_connect_charges_enabled,
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!agency.stripe_connect_account_id) return;
        (async () => {
            try {
                const res = await api.get(`/agencies/${agency.id}/stripe-connect/status`);
                setStatus(res.data);
            } catch { /* keep optimistic */ }
        })();
    }, [agency.id, agency.stripe_connect_account_id]);

    const startOnboard = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/agencies/${agency.id}/stripe-connect/onboard`);
            if (data.onboarding_url) window.location.href = data.onboarding_url;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start onboarding");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="p-5 bg-card" data-testid="agency-stripe-connect-card">
            <div className="flex items-center gap-2 mb-2">
                <Banknote className="h-5 w-5 text-fiji-green" />
                <h3 className="font-display text-base font-bold text-foreground">Direct payouts via Stripe</h3>
            </div>
            <p className="font-app text-xs text-muted-foreground mb-3 leading-relaxed">
                When a JV closes, kickbacks land in your bank automatically — no admin chase, no waiting.
                One-time KYC + bank-detail setup on Stripe.
            </p>
            <div
                data-testid="stripe-connect-au-callout"
                className="mb-4 rounded-md border border-fiji-green/30 bg-fiji-green/5 px-2.5 py-2 text-[11px] leading-snug font-app text-foreground/80"
            >
                <span className="font-semibold text-fiji-green">AU / AUD settlement.</span>{" "}
                Stripe doesn't yet support Fiji, so payouts route through an
                Australian (AUD) Stripe Connect account. Funds convert on receipt — no
                action needed beyond the standard KYC.
            </div>
            {status.payouts_enabled ? (
                <Badge data-testid="stripe-connect-active-badge" className="bg-fiji-green text-white text-[11px]">
                    <BadgeCheck className="h-3 w-3 mr-1" /> Active · payouts enabled
                </Badge>
            ) : status.connected ? (
                <div className="space-y-2">
                    <Badge variant="outline" className="text-[11px] border-coral/40 text-coral">In progress · finish KYC</Badge>
                    <Button onClick={startOnboard} disabled={busy} size="sm" data-testid="stripe-connect-resume-btn" className="w-full bg-coral hover:bg-coral-deep text-white font-app">
                        {busy && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                        Resume KYC <ExternalLink className="h-3 w-3 ml-1.5" />
                    </Button>
                </div>
            ) : (
                <Button onClick={startOnboard} disabled={busy} size="sm" data-testid="stripe-connect-start-btn" className="bg-coral hover:bg-coral-deep text-white font-app">
                    {busy && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                    Connect with Stripe <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
            )}
        </Card>
    );
}

function AgencyTierCard({ agency }) {
    const [tierStatus, setTierStatus] = useState({
        tier: agency.agency_tier || "bronze",
        expires_at: agency.agency_tier_expires_at,
        is_active: false,
    });
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/agencies/${agency.id}/tier/status`);
                setTierStatus(res.data);
            } catch { /* keep optimistic */ }
        })();
    }, [agency.id]);

    const upgrade = async (tier) => {
        setBusy(tier);
        try {
            const { data } = await api.post(`/agencies/${agency.id}/tier/checkout`, {
                tier,
                origin_url: window.location.origin,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start upgrade");
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card className="p-5 bg-card" data-testid="agency-tier-card">
            <div className="flex items-center gap-2 mb-2">
                <Crown className="h-5 w-5 text-coral" />
                <h3 className="font-display text-base font-bold text-foreground">Agency-wide tier subscription</h3>
            </div>
            <p className="font-app text-xs text-muted-foreground mb-4 leading-relaxed">
                Verify your <strong className="text-foreground">whole team</strong> in one purchase.
                <strong className="text-fiji-green"> 30% bulk discount</strong> vs paying per-agent.
            </p>
            {tierStatus.is_active ? (
                <Badge className="bg-fiji-green text-white text-[11px] capitalize" data-testid="agency-tier-active-badge">
                    <BadgeCheck className="h-3 w-3 mr-1" /> {tierStatus.tier} · active
                </Badge>
            ) : (
                <div className="flex gap-2">
                    <Button onClick={() => upgrade("silver")} disabled={!!busy} size="sm" data-testid="agency-tier-silver-btn" className="flex-1 bg-ocean-deep hover:bg-ocean-deep/90 dark:bg-ocean dark:hover:bg-ocean/80 text-white font-app">
                        {busy === "silver" && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                        Silver
                    </Button>
                    <Button onClick={() => upgrade("gold")} disabled={!!busy} size="sm" data-testid="agency-tier-gold-btn" className="flex-1 bg-coral hover:bg-coral-deep text-white font-app">
                        {busy === "gold" && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                        Gold
                    </Button>
                </div>
            )}
        </Card>
    );
}

